import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Button from "@/components/Button";
import apiClient from "@/lib/api-client";
import { trackMetaEvent } from "@/lib/meta-pixel";
import { getAdClickPayload, newMetaEventId } from "@/lib/ad-attribution";
import { Mail, CheckCircle } from "lucide-react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: Record<string, unknown>,
      ) => string;
      reset: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || "0x4AAAAAAC8PFU_OKo0wY4oE";

export default function Register() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    restaurantName: "",
    bookingMode: "tables" as "tables" | "appointments",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [intendedPlan, setIntendedPlan] = useState<{
    name: string;
    price: number;
    interval: string;
  } | null>(null);

  // Anti-bot: record timestamp when form loads
  const formLoadTime = useRef(Date.now());
  const turnstileRef = useRef<HTMLDivElement>(null);

  // Read ?plan / ?mode from the URL (plan pre-selected on the pricing page)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const planId = params.get("plan");

    if (mode === "appointments" || mode === "tables") {
      setFormData((prev) => ({ ...prev, bookingMode: mode }));
    }

    if (planId) {
      // Remember the chosen plan so we can push the user to activate it after the trial
      localStorage.setItem("rm_intended_plan", planId);
      apiClient
        .get("/plans")
        .then((res) => {
          const found = (
            res.data.data as Array<{
              id: string;
              name: string;
              price: number;
              interval: string;
            }>
          )?.find((p) => p.id === planId);
          if (found) {
            setIntendedPlan({
              name: found.name,
              price: found.price,
              interval: found.interval,
            });
          }
        })
        .catch(() => {
          /* non-blocking */
        });
    }
  }, []);

  // Load Turnstile script
  useEffect(() => {
    if (document.getElementById("cf-turnstile-script")) return;
    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  const renderTurnstile = useCallback(() => {
    if (turnstileRef.current && window.turnstile) {
      turnstileRef.current.innerHTML = "";
      window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(null),
        theme: "light",
      });
    }
  }, []);

  useEffect(() => {
    (window as any).onTurnstileLoad = renderTurnstile;
    // If script already loaded
    if (window.turnstile) renderTurnstile();
  }, [renderTurnstile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres");
      return;
    }

    setIsLoading(true);

    // Detect the browser's IANA timezone; if it fails, omit the field and
    // let the backend fall back to its own default (Europe/Madrid).
    let timezone: string | undefined;
    try {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      timezone = undefined;
    }

    const metaEventId = newMetaEventId();
    try {
      const response = await apiClient.post("/auth/register", {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        password: formData.password,
        restaurantName: formData.restaurantName,
        bookingMode: formData.bookingMode,
        timezone,
        adClick: getAdClickPayload(metaEventId),
        // Anti-bot fields
        _ts: String(formLoadTime.current),
        turnstileToken,
      });

      // Only fires for successful account creation and only when marketing
      // consent is active. Do not send PII to Meta.
      trackMetaEvent("CompleteRegistration", {
        content_name: "ReservaManager account",
        content_category:
          formData.bookingMode === "tables" ? "restaurant" : "appointments",
        status: true,
      }, metaEventId);

      // Check if email verification is required
      if (response.data.requiresEmailVerification) {
        setRegistrationComplete(true);
      } else {
        // If no verification required, redirect to dashboard
        if (response.data.token) {
          window.location.href = "/";
        }
      }
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Error al crear la cuenta. Inténtalo de nuevo.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show success message if registration is complete
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="bg-white shadow-md rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Cuenta creada!
            </h2>
            <p className="text-gray-600 mb-6">
              Te hemos enviado un email a{" "}
              <span className="font-medium text-gray-900">
                {formData.email}
              </span>
              . Por favor, haz clic en el enlace de verificación para activar tu
              cuenta.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm">
                <strong>¿No recibes el email?</strong> Revisa tu carpeta de spam
                o correo no deseado.
              </p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center justify-center w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Ir a iniciar sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            src="/logo-horizontal.png"
            alt="ReservaManager"
            className="h-10 w-auto mx-auto mb-4"
          />
          <h2 className="text-center text-2xl font-semibold text-gray-900">
            Crear cuenta
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Empieza a gestionar las reservas de tu negocio hoy mismo
          </p>
        </div>

        <div className="bg-white shadow-md rounded-lg p-8">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          {intendedPlan && (
            <div className="mb-4 p-3 bg-primary-50 border border-primary-200 text-primary-800 rounded-lg text-sm">
              Has elegido el plan <strong>{intendedPlan.name}</strong> (
              {intendedPlan.price}€/
              {intendedPlan.interval === "monthly" ? "mes" : "año"}). Empiezas con
              tu prueba gratuita con todo desbloqueado; podrás activar este plan
              cuando quieras desde tu panel.
            </div>
          )}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ¿Qué tipo de negocio gestionas? *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, bookingMode: "tables" })
                  }
                  className={`relative cursor-pointer rounded-lg border-2 p-4 flex flex-col items-center text-center transition-all hover:shadow-md ${
                    formData.bookingMode === "tables"
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span className="text-3xl mb-2">🍽️</span>
                  <span className="font-semibold text-gray-900">
                    Restaurante
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Reservas con mesas, salas y aforo
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, bookingMode: "appointments" })
                  }
                  className={`relative cursor-pointer rounded-lg border-2 p-4 flex flex-col items-center text-center transition-all hover:shadow-md ${
                    formData.bookingMode === "appointments"
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span className="text-3xl mb-2">💇</span>
                  <span className="font-semibold text-gray-900">
                    Citas previas
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    Peluquerías, clínicas, talleres…
                  </span>
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="restaurantName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                {formData.bookingMode === "appointments"
                  ? "Nombre del negocio *"
                  : "Nombre del Restaurante *"}
              </label>
              <input
                id="restaurantName"
                name="restaurantName"
                type="text"
                required
                value={formData.restaurantName}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder={
                  formData.bookingMode === "appointments"
                    ? "Ej: Peluquería Estilo Único"
                    : "Mi Restaurante"
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Nombre *
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Juan"
                />
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Apellidos *
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="García"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Correo electrónico *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Teléfono
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="+34 600 000 000"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Contraseña *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Mínimo 6 caracteres"
              />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Confirmar contraseña *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Repite tu contraseña"
              />
            </div>

            {/* Honeypot - invisible to real users, bots will fill this */}
            <div
              className="absolute left-[-9999px] top-[-9999px]"
              aria-hidden="true"
            >
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {/* Cloudflare Turnstile */}
            <div ref={turnstileRef} className="flex justify-center" />

            <div className="pt-2">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !turnstileToken}
              >
                {isLoading ? "Creando cuenta..." : "Crear cuenta"}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿Ya tienes cuenta?{" "}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Inicia sesión
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500">
          Al crear una cuenta, aceptas nuestros{" "}
          <a
            href="https://reservamanager.com/terminos"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
          >
            Términos de Servicio
          </a>{" "}
          y{" "}
          <a
            href="https://reservamanager.com/privacidad"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline"
          >
            Política de Privacidad
          </a>
        </p>
      </div>
    </div>
  );
}